/**
 * Misconception Registry — Central catalog of known misconceptions.
 *
 * PEDAGOGICAL_CONTRACT_SPEC §10.2 — Centralized registry with module-prefix
 * convention (rek.*, spr.*, etc.) to prevent synonyms and enable governance
 * validation of misconception tags in seeds.
 *
 * Usage:
 * - Seed authors reference misconception IDs from this registry
 * - Governance pipeline validates that tags in seeds exist in this registry
 * - Admin UI can display human-readable labels for misconception tags
 */

export interface MisconceptionDefinition {
  /** Machine-readable ID (e.g., 'rek.carry-forgotten') */
  id: string
  /** Human-readable label (Dutch) */
  label: string
  /** Module scope */
  module: string
  /** Brief description of the misconception */
  description: string
  /** Common question families where this misconception appears */
  typicalFamilies?: string[]
}

/**
 * Central misconception registry.
 * Organized by module prefix for clarity.
 */
export const MISCONCEPTION_REGISTRY: MisconceptionDefinition[] = [
  // ── Basisrekenen (REK) ──────────────────────────────────────────────────
  {
    id: 'rek.carry-forgotten',
    label: 'Onthouden vergeten',
    module: 'Basisrekenen',
    description:
      'Leerling vergeet de overdracht (carry) bij optellen of vermenigvuldigen.',
    typicalFamilies: ['REK-CIJF-PLUS', 'REK-CIJF-KEER'],
  },
  {
    id: 'rek.borrow-forgotten',
    label: 'Lenen vergeten',
    module: 'Basisrekenen',
    description:
      'Leerling vergeet te lenen bij aftrekken, trekt kleinste van grootste af per kolom.',
    typicalFamilies: ['REK-CIJF-MIN'],
  },
  {
    id: 'rek.order-of-operations',
    label: 'Volgorde verkeerd',
    module: 'Basisrekenen',
    description:
      'Leerling past de bewerkingsvolgorde (Meneer Van Dansen) niet correct toe.',
    typicalFamilies: ['REK-VOLGORDE'],
  },
  {
    id: 'rek.multiplication-table-error',
    label: 'Tafelfout',
    module: 'Basisrekenen',
    description:
      'Leerling kent een specifieke tafel niet uit het hoofd (bijv. 7×8=54).',
    typicalFamilies: ['REK-TAFELS-BASIS', 'REK-TAFELS-UITBREIDEN'],
  },
  {
    id: 'rek.division-remainder-ignored',
    label: 'Rest genegeerd',
    module: 'Basisrekenen',
    description:
      'Leerling negeert de rest bij een deling of rondt verkeerd af.',
    typicalFamilies: ['REK-CIJF-DEEL'],
  },

  // ── Getalbegrip (GET) ───────────────────────────────────────────────────
  {
    id: 'get.place-value-swap',
    label: 'Plaatswaarde verwisseld',
    module: 'Getalbegrip',
    description:
      'Leerling verwisselt tientallen en eenheden, of honderdtallen en tientallen.',
    typicalFamilies: ['GET-GROOT-LEZEN', 'GET-GROOT-POS'],
  },
  {
    id: 'get.decimal-align',
    label: 'Komma niet uitgelijnd',
    module: 'Getalbegrip',
    description:
      'Leerling lijnt kommagetallen niet uit bij optellen/aftrekken.',
    typicalFamilies: ['GET-KOMMA-PLUS', 'GET-KOMMA-MIN'],
  },
  {
    id: 'get.fraction-whole-comparison',
    label: 'Breuk als geheel getal vergeleken',
    module: 'Getalbegrip',
    description:
      'Leerling vergelijkt teller en noemer apart i.p.v. de breukwaarde.',
    typicalFamilies: ['GET-BREUK-BASIS', 'GET-VERGELIJK'],
  },
  {
    id: 'get.percentage-of-vs-increase',
    label: 'Percentage van vs. toename',
    module: 'Getalbegrip',
    description:
      'Leerling verwart "X% van" met "X% toename" (bijv. 20% van 100 vs. 100 + 20%).',
    typicalFamilies: ['GET-PROC-VAN', 'GET-PROC-TOENAME'],
  },
  {
    id: 'get.negative-subtraction-direction',
    label: 'Negatief aftrekken omgedraaid',
    module: 'Getalbegrip',
    description:
      'Leerling draait de richting om bij aftrekken met negatieve getallen.',
    typicalFamilies: ['GET-NEGATIEF-MIN'],
  },

  // ── Spelling Regels (SPR) ───────────────────────────────────────────────
  {
    id: 'spr.dt-confusion',
    label: 'D/T-fout',
    module: 'SpellingRegel',
    description:
      'Leerling past de d/t-regel verkeerd toe bij werkwoordsvormen.',
    typicalFamilies: ['SPR-WEET-D-T', 'SPR-WEET-DT'],
  },
  {
    id: 'spr.double-consonant',
    label: 'Verdubbeling vergeten',
    module: 'SpellingRegel',
    description:
      'Leerling vergeet de medeklinkerverdubbeling (bijv. "baken" i.p.v. "bakken").',
    typicalFamilies: ['SPR-KLANK-BAKKER', 'SPR-KLANK-VERDUBBEL'],
  },
  {
    id: 'spr.ei-ij-confusion',
    label: 'Ei/ij verwisseld',
    module: 'SpellingRegel',
    description: 'Leerling verwisselt ei en ij in woorden.',
    typicalFamilies: ['SPR-WEET-EI-IJ'],
  },
  {
    id: 'spr.open-closed-syllable',
    label: 'Open/gesloten lettergreep',
    module: 'SpellingRegel',
    description:
      'Leerling herkent het verschil niet tussen open en gesloten lettergrepen.',
    typicalFamilies: ['SPR-KLANK-JAGER', 'SPR-KLANK-VERKORTEN'],
  },
  {
    id: 'spr.au-ou-confusion',
    label: 'Au/ou verwisseld',
    module: 'SpellingRegel',
    description: 'Leerling verwisselt au en ou in woorden.',
    typicalFamilies: ['SPR-KLANK-AU-OU'],
  },

  // ── Spelling Werkwoorden (SWW) ──────────────────────────────────────────
  {
    id: 'sww.kofschip-misapplied',
    label: 'Kofschip verkeerd toegepast',
    module: 'SpellingWW',
    description:
      'Leerling past de kofschip-regel verkeerd toe bij voltooid deelwoord.',
    typicalFamilies: ['SWW-VD-KOF', 'SWW-VT-ZWAK-KOF'],
  },
  {
    id: 'sww.stem-extraction-error',
    label: 'Stam verkeerd bepaald',
    module: 'SpellingWW',
    description:
      'Leerling bepaalt de stam van het werkwoord verkeerd (bijv. "leeft" → stam "leef" i.p.v. "leev").',
    typicalFamilies: ['SWW-TT-STAM', 'SWW-TT-DSTAM'],
  },
  {
    id: 'sww.past-participle-ge-omitted',
    label: 'Ge- weggelaten',
    module: 'SpellingWW',
    description:
      'Leerling vergeet ge- bij het voltooid deelwoord (of voegt het onterecht toe).',
    typicalFamilies: ['SWW-VD-ZONDER-GE'],
  },
  {
    id: 'sww.irregular-verb-regularized',
    label: 'Onregelmatig werkwoord vervoegd als regelmatig',
    module: 'SpellingWW',
    description:
      'Leerling past regelmatige vervoegingsregels toe op een onregelmatig werkwoord.',
    typicalFamilies: ['SWW-VT-ONREGELMATIG', 'SWW-VD-STERK'],
  },

  // ── Metriek (MET) ──────────────────────────────────────────────────────
  {
    id: 'met.unit-factor-10',
    label: 'Factor 10 fout',
    module: 'Metriek',
    description:
      'Leerling vermenigvuldigt of deelt met de verkeerde factor van 10 bij eenheidsomrekening.',
    typicalFamilies: ['MET-LEN-OMREKEN', 'MET-GEW-OMREKEN', 'MET-INH-OMREKEN'],
  },
  {
    id: 'met.area-perimeter-confused',
    label: 'Oppervlakte/omtrek verward',
    module: 'Metriek',
    description:
      'Leerling verwart oppervlakte met omtrek (bijv. l×b i.p.v. 2×(l+b)).',
    typicalFamilies: ['MET-OPP-RECHT', 'MET-OMTREK-RECHT'],
  },
  {
    id: 'met.scale-direction',
    label: 'Schaal verkeerde richting',
    module: 'Metriek',
    description:
      'Leerling vermenigvuldigt i.p.v. deelt (of omgekeerd) bij schaalberekeningen.',
    typicalFamilies: ['MET-SCHAAL-BASIS', 'MET-SCHAAL-LENGTE'],
  },

  // ── Tijd (TIJD) ─────────────────────────────────────────────────────────
  {
    id: 'tijd.am-pm-swap',
    label: 'AM/PM verwisseld',
    module: 'Tijd',
    description: 'Leerling verwisselt ochtend en middag bij 12-uursnotatie.',
    typicalFamilies: ['TGL-TIJD-12-24'],
  },
  {
    id: 'tijd.half-hour-direction',
    label: 'Half uur verkeerde richting',
    module: 'Tijd',
    description:
      'Leerling leest "half drie" als 3:30 i.p.v. 2:30 (Nederlands half-uur convention).',
    typicalFamilies: ['TGL-TIJD-ANALOOG'],
  },
  {
    id: 'tijd.duration-vs-endpoint',
    label: 'Duur vs. eindtijd',
    module: 'Tijd',
    description:
      'Leerling verwart de duur van een activiteit met het eindtijdstip.',
    typicalFamilies: ['TGL-TIJD-DUUR'],
  },

  // ── Geld (GELD) ─────────────────────────────────────────────────────────
  {
    id: 'geld.decimal-comma',
    label: 'Komma vergeten/verkeerd',
    module: 'Geld',
    description:
      'Leerling plaatst de decimale komma verkeerd bij geldbedragen.',
    typicalFamilies: ['TGL-GELD-KOMMA'],
  },
  {
    id: 'geld.change-calculation',
    label: 'Wisselgeld verkeerd berekend',
    module: 'Geld',
    description:
      'Leerling berekent wisselgeld verkeerd (trekt van het verkeerde bedrag af).',
    typicalFamilies: ['TGL-GELD-TERUG', 'TGL-GELD-TERUGREKENEN'],
  },

  // ── Taalbegrip (TAA) ────────────────────────────────────────────────────
  {
    id: 'taa.main-idea-vs-detail',
    label: 'Hoofdgedachte vs. detail',
    module: 'Taalbegrip',
    description:
      'Leerling verwart een ondersteunend detail met de hoofdgedachte van een tekst.',
    typicalFamilies: ['TAA-LEZEN-KERN', 'TAA-LEZEN-DETAIL'],
  },
  {
    id: 'taa.fact-vs-opinion',
    label: 'Feit vs. mening',
    module: 'Taalbegrip',
    description:
      'Leerling kan geen onderscheid maken tussen feiten en meningen in een tekst.',
    typicalFamilies: ['TAA-LEZEN-FEIT'],
  },
  {
    id: 'taa.subject-predicate-confused',
    label: 'Onderwerp/gezegde verward',
    module: 'Taalbegrip',
    description: 'Leerling verwart het onderwerp met het gezegde in een zin.',
    typicalFamilies: ['TAA-ONT-GEZEGDE', 'TAA-ONT-BASIS'],
  },
]

// ─── Lookup helpers ───────────────────────────────────────────────────────────

const _byId = new Map<string, MisconceptionDefinition>()
for (const m of MISCONCEPTION_REGISTRY) {
  _byId.set(m.id, m)
}

/** Get a misconception definition by ID. Returns undefined if not found. */
export function getMisconception(
  id: string,
): MisconceptionDefinition | undefined {
  return _byId.get(id)
}

/** Check if a misconception ID exists in the registry. */
export function isKnownMisconception(id: string): boolean {
  return _byId.has(id)
}

/** Get all misconceptions for a given module. */
export function getMisconceptionsForModule(
  module: string,
): MisconceptionDefinition[] {
  return MISCONCEPTION_REGISTRY.filter((m) => m.module === module)
}

/** Get all unique misconception IDs. */
export function getAllMisconceptionIds(): string[] {
  return MISCONCEPTION_REGISTRY.map((m) => m.id)
}
